import { useMutation } from '@apollo/client/react';
import { Alert, Button, Col, Form, Row, Tag } from 'antd';
import clsx from 'clsx';
import { capitalize, filter, forEach, includes, keys, map } from 'lodash';
import React, { useContext, useState } from 'react';
import { AppContext } from '../../../../../../../../AppContext';
import { toast } from '../../../../../../../../apollo';
import {
  ADDONS,
  BULK_ACTION,
  EQC_ALLOWED_MODULE,
  EQC_CONFIG_LABELS,
  PROJECT_CHECKLIST_STATUS,
  REQUEST_FEATURE_UPGRADE_KEYS,
  ROUTES,
  STAGE_ITEM_SEQUENCE,
  TAB_KEYS,
} from '../../../../../../../../common/constants';
import useRouter from '../../../../../../../../common/useRouter';
import CommonBulkConfirmationModal from '../../../../../../../../components/CommonBulkConfirmationModal';
import CommonDrawer from '../../../../../../../../components/CommonDrawer';
import CommonSelect from '../../../../../../../../components/CommonSelect';
import UpgradeModal from '../../../../../../../../components/UpgradeModal';
import {
  BULK_UPDATE_PROJECT_EQC_TYPE_STAGE_SETTINGS,
  CREATE_PROJECT_RFI_DRAFT,
} from '../../../../../../graphql/Mutation';
import RfiActivationRequiredModal from '../../../rfi/RfiActivationRequiredModal';

const BulkEditSettingsDrawer = ({
  showDrawer,
  setShowDrawer,
  selectedRows,
  isSelectAllAcrossPages,
  exceptIds,
  paginationProp,
  getBulkProjectStageJobStatus,
  startJobTracking,
  onSuccess,
  projectEqcTypeId,
  eqcTypeData,
  projectId,
}) => {
  const [form] = Form.useForm();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showRfiActivationRequiredModal, setShowRfiActivationRequiredModal] =
    useState(false);
  const { getTenantUser } = useContext(AppContext);
  const currentTenant = getTenantUser();
  const { navigate } = useRouter();

  const [createProjectRFIDraft] = useMutation(CREATE_PROJECT_RFI_DRAFT, {
    onCompleted(res) {
      const projectRFIId = res?.createProjectRFIDraft?.projectRFIId;
      if (!projectRFIId) {
        return;
      }
      setShowRfiActivationRequiredModal(false);
      navigate(
        `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.RFI}/${res?.createProjectRFIDraft?.projectRFIId}/draft`,
      );
    },
    onError() {},
  });

  const selectedCount = isSelectAllAcrossPages
    ? (paginationProp?.total || 0) - (exceptIds?.length || 0)
    : selectedRows?.length || 0;

  const drawerTitle = `Bulk Edit Settings (${selectedCount} Stages Selected)`;

  const checklistConfigLabels = filter(EQC_CONFIG_LABELS, (type) =>
    includes(type?.allowed_module, EQC_ALLOWED_MODULE.CHECKLIST),
  );

  const [bulkUpdateSettings, { loading }] = useMutation(
    BULK_UPDATE_PROJECT_EQC_TYPE_STAGE_SETTINGS,
    {
      onCompleted(response) {
        const { status, jobId } =
          response?.bulkUpdateProjectEqcTypeStageSettings || {};

        if (status === 'SUCCESS' && jobId) {
          startJobTracking(jobId, BULK_ACTION.EDIT_SETTINGS);
          getBulkProjectStageJobStatus({
            variables: { jobId },
          });
        }

        onSuccess?.();
        setShowDrawer(false);
      },
      onError() {},
    },
  );

  const buildSettingsMutationData = (values) => {
    const checklistConfigKeys = filter(EQC_CONFIG_LABELS, (type) =>
      includes(type?.allowed_module, EQC_ALLOWED_MODULE.CHECKLIST),
    )?.map((item) => item?.key);

    const formattedSettings = {};
    let hasAtLeastOneValue = false;

    forEach(checklistConfigKeys, (key) => {
      const value = values?.[key];
      if (value === undefined || value === null) return;

      hasAtLeastOneValue = true;

      if (key === 'skip') {
        formattedSettings.skipStage = value;
      } else if (key === 'pass') {
        formattedSettings.bypassStage = value;
      } else if (key === 'itemOrderType') {
        formattedSettings.itemOrderType = value;
      } else {
        formattedSettings[key] = value;
      }
    });

    return { formattedSettings, hasAtLeastOneValue };
  };

  const onFormSubmit = async (values) => {
    try {
      const { formattedSettings, hasAtLeastOneValue } =
        buildSettingsMutationData(values);

      if (!hasAtLeastOneValue) {
        toast({
          message: 'No updates made. Please make changes to save.',
          type: 'error',
        });
        return;
      }

      const mutationData = {
        ...formattedSettings,
        projectEqcTypeId: Number(projectEqcTypeId),
      };

      if (isSelectAllAcrossPages) {
        mutationData.isAllSelected = true;
        if (exceptIds?.length > 0) {
          mutationData.exceptProjectEqcTypeStageIds = exceptIds;
        }
      } else {
        mutationData.projectEqcTypeStageIds =
          selectedRows?.map((row) => row?.id) || [];
      }

      await bulkUpdateSettings({
        variables: {
          data: mutationData,
        },
      });
    } catch (error) {
      return error;
    }
  };

  const handleCancel = () => {
    const hasFormChanges = form.isFieldsTouched();

    if (hasFormChanges) {
      setShowConfirmationModal(true);
    } else {
      form.resetFields();
      setShowDrawer(false);
    }
  };

  const handleConfirmDiscard = () => {
    form.resetFields();
    setShowDrawer(false);
  };

  const BulkSettingTagSelect = ({ value, onChange, item, idx }) => {
    const handleTagChange = (tagValue) => {
      const newValue = value === tagValue ? undefined : tagValue;

      if (item?.key === 'isRfiActive' && newValue === true) {
        if (!includes(currentTenant?.tenant?.addOn, ADDONS.RFI)) {
          setShowUpgradeModal(true);
          onChange?.(undefined);
          return;
        }
        if (
          eqcTypeData?.project?.projectRFIs?.[0]?.status !==
          PROJECT_CHECKLIST_STATUS.PUBLISHED
        ) {
          setShowRfiActivationRequiredModal(true);
          onChange?.(undefined);
          return;
        }
      }

      onChange?.(newValue);
    };

    return (
      <div className="bulk-settings-tag-group" id={`bulk-setting-${idx}-tag`}>
        <Tag.CheckableTag
          className="bulk-tag-on"
          checked={value === true}
          onChange={() => handleTagChange(true)}
        >
          On
        </Tag.CheckableTag>
        <Tag.CheckableTag
          className="bulk-tag-off"
          checked={value === false}
          onChange={() => handleTagChange(false)}
        >
          Off
        </Tag.CheckableTag>
      </div>
    );
  };

  return (
    <>
      <CommonDrawer
        title={drawerTitle}
        open={showDrawer}
        onClose={handleCancel}
        width={800}
        getContainer={() =>
          // eslint-disable-next-line no-undef
          document.getElementById('stage-list-container') || document.body
        }
      >
        <Form form={form} layout="vertical" onFinish={onFormSubmit}>
          <div className="bulk-settings-step stage-config">
            <Alert
              title="Changes will apply only where updates are made. All other configurations will remain unchanged."
              type="info"
              showIcon
              className="mb-16"
            />
            {map(checklistConfigLabels, (item, idx) => {
              return (
                <div className={clsx('config-div')} key={item?.key}>
                  <Row
                    justify="space-between notification-row"
                    align="middle"
                    gutter={{ xs: 0, sm: 30 }}
                  >
                    <Col xs={12} sm={16}>
                      <div className="title">{item?.label}</div>
                      <div className="description">{item?.description}</div>
                    </Col>
                    <Col>
                      <div className="d-flex justify-center align-center">
                        {item?.key === 'itemOrderType' ? (
                          <div className="width-150">
                            <Form.Item name={item?.key} className="nested-form">
                              <CommonSelect
                                placeholder="Checkpoint flow"
                                allowClear
                                options={[
                                  ...map(
                                    keys(STAGE_ITEM_SEQUENCE),
                                    (status, index) => {
                                      return {
                                        key: index,
                                        value: status,
                                        label: capitalize(
                                          STAGE_ITEM_SEQUENCE[status],
                                        ),
                                      };
                                    },
                                  ),
                                ]}
                                id={`bulk-setting-${idx}-select`}
                              />
                            </Form.Item>
                          </div>
                        ) : (
                          <Form.Item name={item?.key} className="nested-form">
                            <BulkSettingTagSelect item={item} idx={idx} />
                          </Form.Item>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              );
            })}
          </div>
          <div className="form-buttons">
            <Button
              shape="round"
              className="cancel-button"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              shape="round"
              type="primary"
              className="save-button"
              loading={loading}
              htmlType="submit"
            >
              Save
            </Button>
          </div>
        </Form>
      </CommonDrawer>

      {showConfirmationModal && (
        <CommonBulkConfirmationModal
          showModal={showConfirmationModal}
          setShowModal={setShowConfirmationModal}
          onConfirm={handleConfirmDiscard}
          title="Unsaved Changes"
          message="You have changes that haven't been saved. Discard them?"
        />
      )}
      {showUpgradeModal && (
        <UpgradeModal
          showModal={showUpgradeModal}
          setShowModal={setShowUpgradeModal}
          projectId={projectId}
          featureKey={REQUEST_FEATURE_UPGRADE_KEYS.RFI}
          isAlreadyRequested={eqcTypeData?.project?.isRfiRequestSent}
        />
      )}
      {showRfiActivationRequiredModal && (
        <RfiActivationRequiredModal
          projectSetupRequired
          showModal={showRfiActivationRequiredModal}
          setShowModal={setShowRfiActivationRequiredModal}
          eqcTypeData={eqcTypeData}
          onOk={() => {
            createProjectRFIDraft({
              variables: {
                data: { projectId },
              },
            });
          }}
        />
      )}
    </>
  );
};

export default BulkEditSettingsDrawer;
